import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };
type StudentGroupItem = { id: string; name: string };

export type ActiveStudentsByClassReportSearch = {
  classTypeId?: string;
  levelId?: string;
  groupId?: string;
  sortBy?: "count" | "className";
  includeEmpty?: "true" | "false";
};

const SORT_OPTIONS = [
  { value: "count", label: "ຈຳນວນນັກຮຽນ (ຫຼາຍ → ໜ້ອຍ)" },
  { value: "className", label: "ຊື່ຄລາສ" },
];

export function ActiveStudentsByClassReportFilter() {
  const nav = useNavigate({ from: "/app/reports/active-students-by-class" });
  const search = useSearch({
    from: "/app/reports/active-students-by-class",
  }) as ActiveStudentsByClassReportSearch;

  const [classTypeId, setClassTypeId] = useState(search.classTypeId ?? "");
  const [levelId, setLevelId] = useState(search.levelId ?? "");
  const [groupId, setGroupId] = useState(search.groupId ?? "");
  const [sortBy, setSortBy] = useState<"count" | "className">(
    search.sortBy ?? "count",
  );

  useEffect(() => {
    setClassTypeId(search.classTypeId ?? "");
    setLevelId(search.levelId ?? "");
    setGroupId(search.groupId ?? "");
    setSortBy(search.sortBy ?? "count");
  }, [search.classTypeId, search.levelId, search.groupId, search.sortBy]);

  const hasActiveFilters = Boolean(
    classTypeId || levelId || groupId || search.sortBy === "className",
  );

  const applyFilters = (
    updates: Partial<ActiveStudentsByClassReportSearch> = {},
  ) => {
    const next: ActiveStudentsByClassReportSearch = {
      classTypeId:
        updates.classTypeId !== undefined
          ? updates.classTypeId
          : classTypeId || undefined,
      levelId:
        updates.levelId !== undefined
          ? updates.levelId
          : levelId || undefined,
      groupId:
        updates.groupId !== undefined ? updates.groupId : groupId || undefined,
      sortBy: updates.sortBy ?? sortBy,
      includeEmpty: "true",
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <InfiniteCombobox<ClassTypeItem>
          queryKey={["master", "class-types", "lookup"]}
          queryFn={(args) =>
            fetchLookupForInfinite<ClassTypeItem>(
              `${config.apiUrl}/master/class-types/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<ClassTypeItem>(
              `${config.apiUrl}/master/class-types/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={classTypeId}
          onValueChange={(v) => {
            setClassTypeId(v);
            applyFilters({ classTypeId: v || undefined });
          }}
          placeholder="ປະເພດຄລາດສ໌..."
          className="w-[200px]"
        />
        <InfiniteCombobox<LevelItem>
          queryKey={["master", "levels", "lookup"]}
          queryFn={(args) =>
            fetchLookupForInfinite<LevelItem>(
              `${config.apiUrl}/master/levels/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<LevelItem>(
              `${config.apiUrl}/master/levels/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={levelId}
          onValueChange={(v) => {
            setLevelId(v);
            applyFilters({ levelId: v || undefined });
          }}
          placeholder="ລະດັບ..."
          className="w-[180px]"
        />
        <InfiniteCombobox<StudentGroupItem>
          queryKey={["master", "student-groups", "lookup"]}
          queryFn={(args) =>
            fetchLookupForInfinite<StudentGroupItem>(
              `${config.apiUrl}/master/student-groups/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<StudentGroupItem>(
              `${config.apiUrl}/master/student-groups/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={groupId}
          onValueChange={(v) => {
            setGroupId(v);
            applyFilters({ groupId: v || undefined });
          }}
          placeholder="ກຸ່ມນັກຮຽນ..."
          className="w-[200px]"
        />
        <SimpleSelect
          value={sortBy}
          onValueChange={(v) => {
            const next = (v as "count" | "className") || "count";
            setSortBy(next);
            applyFilters({ sortBy: next });
          }}
          options={SORT_OPTIONS}
          placeholder="ຈັດລຽງ..."
          className="w-[220px]"
        />
        {hasActiveFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setClassTypeId("");
              setLevelId("");
              setGroupId("");
              setSortBy("count");
              nav({
                search: { includeEmpty: "true", sortBy: "count" },
              });
            }}
          >
            <XIcon className="mr-1 size-4" />
            ລ້າງຕົວກອງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
